import React, { useEffect, useMemo, useState } from "react";
import HeaderContainer from "../../components/container/HeaderContainer";
import Backlink from "../../components/Backlink";
import { useUser } from "../../context/UserContext";
import { useMutation } from "@tanstack/react-query";
import CustomizedModal from "../../components/UI/CustomizedModal";
import { dataImport } from "../../services/AdminServices";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import DisplayTable from "../../components/tables/DisplayTable";
import { ImportFilesBaseStructure } from "../../components/tools/importFiles/ImportFilesBaseStructure";
import { ImportFilesConfirmation } from "../../components/tools/importFiles/ImportFilesConfirmation";
import { FaCheckCircle } from "react-icons/fa";
import { tableColumnWrap } from "../../components/tables/tableStyles";
const Sample_Field_Data_File = "/sample_field_data_file.csv";

export const ImportFieldData = () => {
  const { userInfo } = useUser();
  const { user_email } = userInfo;
  const [customModal, setCustomModal] = useState();
  const [inputFile, setInputFile] = useState({
    name: "",
  });
  const [formData, setFormData] = useState(null);

  const [response, setResponse] = useState({});

  const mutation = useMutation({
    mutationFn: dataImport,
    onError: (error) => {
      setCustomModal({
        title: "Error Occured",
        message: "Something went wrong, Please contact administrator.",
      });
    },
  });

  useEffect(() => {
    if (mutation?.data?.data) {
      setResponse(mutation?.data?.data);
    }
  }, [mutation?.data?.data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "row_number",
        header: "row #",
        size: 15
      },
      {
        accessorKey: "global_id",
        header: "global_id",
        size: 100,
        Cell: ({ cell }) => {
          const global_id = cell.getValue();
          if (global_id === null) return;
          return <div style={tableColumnWrap("unset")}>{global_id}</div>;
        }
      },
      {
        accessorKey: "ps_code",
        header: "ps_code",
        size: 60,
        Cell: ({ cell }) => {
          const ps_code = cell.getValue();
          if (ps_code === null) return;
          return <div style={tableColumnWrap()}>{ps_code}</div>;
        }
      },
      {
        accessorKey: "visit_date",
        header: "visit_date",
        size: 50,
        Cell: ({ cell }) => {
          const visit_date = cell.getValue();
          if (visit_date === null) return;
          return <div style={tableColumnWrap()}>{visit_date}</div>;
        }
      },
      {
        accessorKey: "error",
        header: "error",
        size: 100,
        Cell: ({ cell }) => {
          const error = cell.getValue();
          if (error === null) return;
          const errors = error.split(';').map(err => err.trim()).filter(err => err);
          return (
            <div style={tableColumnWrap("unset")}>
              {errors.map((err, index) => (
                <React.Fragment key={index}>
                  <div>{err}</div>
                  {index < errors.length - 1 && <div style={{ height: '8px' }} />}
                </React.Fragment>
              ))}
            </div>
          );
        },
      }
    ],
    [response]
  );

  const insertFieldData = () => {
    setFormData(prev => {
      if (prev instanceof FormData) {
        const form = prev;
        form.append("import_type", "field_data");
        form.append("import_by", user_email);
        form.append("commit", true);
        return form;
      }
      return prev;
    });

    mutation.mutate(formData);
  };

  return (
    <>
      {customModal && (
        <CustomizedModal
          title={customModal.title}
          message={customModal.message}
          onCancel={() => setCustomModal(null)}
        />
      )}
      <section className="w-full text-primary">
        <Backlink>Back</Backlink>
        <section className="my-8 text-primary w-full">
          <HeaderContainer title="Sample Field Form Bulk Data Import">
            <ImportFilesBaseStructure
              title={"Upload the Field Data CSV file to import field data."}
              sampleFileInfo={
                "File must be comma-separated, and first row must contain field names as present in the import file template."
              }
              sampleFilePath={Sample_Field_Data_File}
              sampleFileText={"Sample Field Data Import File"}
              inputFile={inputFile}
              import_type={"field_data"}
              setInputFile={setInputFile}
              setFormData={setFormData}
              setResponse={setResponse}
              mutation={mutation}
            />

            {/* Inserted Rows */}
            {inputFile?.name &&
              response?.imported_records > 0 && (
                <>
                  <p className="mt-5 italic flex">
                    <FaCheckCircle className="text-green-500 w-[20px] h-[20px] mr-2" />
                    {response?.imported_records} of{" "}
                    {response?.total_records} records
                    updated.
                  </p>
                </>
              )}

            {/* Confirmation */}
            {inputFile?.name &&
              response?.valid_records > 0 &&
              !mutation.isError &&
              response?.imported_records === 0 && (
                <ImportFilesConfirmation
                  total_rows={response?.valid_records}
                  confirmation_text={
                    ` record(s) out of ${response?.total_records} are needed to be updated. Would you like to proceed with the update?`
                  }
                  onConfirm={insertFieldData}
                  onCancel={() => {
                    setInputFile({ name: "" });
                    setResponse({});
                  }}
                />
              )
            }

            {mutation?.isPending ? (
              <LoadingSpinner size="30px" enableLoadingText={true} />
            ) : (
              <>

                {/* invalid Rows */}
                {inputFile.name && response?.invalid_records?.length > 0 && (
                  <>
                    <p className="mt-5 pb-2">
                      {response?.invalid_records.length} records found with errors
                    </p>
                    <DisplayTable
                      tableData={
                        response?.invalid_records
                      }
                      columns={columns}
                      showExportButton={true}
                    />
                  </>
                )}

                {/* No updates needed */}
                {inputFile.name && response?.valid_records === 0 && response?.invalid_records?.length === 0 && (
                  <p className="mt-5 italic flex">
                    <FaCheckCircle className="text-green-500 w-[20px] h-[20px] mr-2" />
                    No updates needed. All records are already up to date.
                  </p>
                )}

              </>
            )}
          </HeaderContainer>
        </section>
      </section>
    </>
  );
};

export default ImportFieldData;